import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import StrategicAssetAllocation from '../components/StrategicAssetAllocation';
import assetClassData from '../data/assetClassAssumptions.json';
import type { Allocation } from '../types/simulation';

interface Goal {
  name: string;
  initialAmount: number;
  targetAmount: number;
  monthlyContribution: number;
  timeHorizon: number;
  riskTolerance: number;
  allocation: Allocation;
}

const defaultAllocation: Allocation = {
  cash: 0,
  'fixed income': 0,
  equities: 0,
  'real assets': 0,
  alternatives: 0,
  crypto: 0
};

const GoalAnalysisPage: React.FC = () => {
  const { user } = useAuth();
  const username = user?.name || user?.nickname;
  const [goals, setGoals] = useState<Goal[]>([]);
  const [currentGoal, setCurrentGoal] = useState<Goal>({
    name: '',
    initialAmount: 0,
    targetAmount: 0,
    monthlyContribution: 0,
    timeHorizon: 10,
    riskTolerance: 5,
    allocation: { ...defaultAllocation }
  });
  const [selectedGoalIndex, setSelectedGoalIndex] = useState<number | null>(null);

  const handleAddGoal = () => {
    if (currentGoal.name && currentGoal.initialAmount > 0) {
      // Check if we're updating an existing goal
      const existingGoalIndex = goals.findIndex(g => g.name === currentGoal.name);
      
      if (existingGoalIndex >= 0) {
        // Update existing goal
        const updatedGoals = [...goals];
        updatedGoals[existingGoalIndex] = currentGoal;
        setGoals(updatedGoals);
      } else {
        // Add new goal
        setGoals([...goals, currentGoal]);
      }

      // Reset form
      setCurrentGoal({
        name: '',
        initialAmount: 0,
        targetAmount: 0,
        monthlyContribution: 0,
        timeHorizon: 10,
        riskTolerance: 5,
        allocation: { ...defaultAllocation }
      });
    }
  };

  const handleSelectGoal = (index: number) => {
    setSelectedGoalIndex(index);
    // Auto-trigger recalculation when selecting a goal for the first time
    if (!goals[index].allocation.equities) {
      const updatedGoals = [...goals];
      const defaultAllocation = calculateDefaultAllocation(goals[index].riskTolerance, goals[index].timeHorizon);
      updatedGoals[index] = {
        ...updatedGoals[index],
        allocation: defaultAllocation
      };
      setGoals(updatedGoals);
    }
  };

  const handleLoadGoal = (e: React.MouseEvent, goal: Goal) => {
    e.stopPropagation(); // Prevent goal selection when clicking load button
    setCurrentGoal(goal);
  };

  const calculateDefaultAllocation = (riskTolerance: number, timeHorizon: number): Allocation => {
    const riskScore = (riskTolerance / 10);
    const timeScore = (timeHorizon / 30);
    const combinedScore = (riskScore * 0.7 + timeScore * 0.3);
    
    const defensiveTotal = 60 * (1 - combinedScore);
    const growthTotal = 100 - defensiveTotal;

    return {
      cash: Math.round(defensiveTotal * 0.33),
      'fixed income': Math.round(defensiveTotal * 0.67),
      equities: Math.round(growthTotal * 0.55),
      'real assets': Math.round(growthTotal * 0.25),
      alternatives: Math.round(growthTotal * 0.15),
      crypto: Math.round(growthTotal * 0.05)
    };
  };

  const handleAllocationChange = (allocation: Allocation) => {
    if (selectedGoalIndex !== null) {
      const updatedGoals = [...goals];
      updatedGoals[selectedGoalIndex] = {
        ...updatedGoals[selectedGoalIndex],
        allocation
      };
      setGoals(updatedGoals);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>Goal Analysis</h1>
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '8px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ marginTop: 0 }}>Strategic Asset Allocation Tool</h2>
        <p style={{ color: '#666', marginBottom: '15px' }}>
          This tool helps you determine the optimal mix of assets for your investment portfolio based on your:
        </p>
        <ul style={{ color: '#666', marginBottom: '15px' }}>
          <li>Investment goals and target amounts</li>
          <li>Time horizon (when you need the money)</li>
          <li>Risk tolerance (how much volatility you can handle)</li>
        </ul>
        <p style={{ color: '#666' }}>
          Start by defining your investment goal below. You can add multiple goals and get a recommended asset allocation for each one.
          The tool will simulate thousands of potential market scenarios to show you the range of possible outcomes.
        </p>
      </div>
      
      <div style={{ 
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <h2>Define Your Investment Goal</h2>
        <div style={{ display: 'grid', gap: '20px', maxWidth: '600px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Goal Name:
            </label>
            <input
              type="text"
              value={currentGoal.name}
              onChange={(e) => setCurrentGoal({ ...currentGoal, name: e.target.value })}
              placeholder="e.g., Retirement, House Down Payment"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Initial Amount:
            </label>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '8px' }}>$</span>
              <input
                type="number"
                value={currentGoal.initialAmount}
                onChange={(e) => setCurrentGoal({ ...currentGoal, initialAmount: Number(e.target.value) })}
                min="0"
                step="1000"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Target Amount: <span style={{ color: '#666', fontSize: '0.9em' }}>(optional)</span>
            </label>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '8px' }}>$</span>
              <input
                type="number"
                value={currentGoal.targetAmount}
                onChange={(e) => setCurrentGoal({ ...currentGoal, targetAmount: Number(e.target.value) })}
                min="0"
                step="1000"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Monthly Contribution:
            </label>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '8px' }}>$</span>
              <input
                type="number"
                value={currentGoal.monthlyContribution}
                onChange={(e) => setCurrentGoal({ ...currentGoal, monthlyContribution: Number(e.target.value) })}
                min="0"
                step="100"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Time Horizon (years): {currentGoal.timeHorizon}
            </label>
            <input
              type="range"
              min="1"
              max="30"
              value={currentGoal.timeHorizon}
              onChange={(e) => setCurrentGoal({ ...currentGoal, timeHorizon: Number(e.target.value) })}
              style={{ width: '100%' }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Risk Tolerance (1-10): {currentGoal.riskTolerance}
            </label>
            <input
              type="range"
              min="1"
              max="10"
              value={currentGoal.riskTolerance}
              onChange={(e) => setCurrentGoal({ ...currentGoal, riskTolerance: Number(e.target.value) })}
              style={{ width: '100%' }}
            />
          </div>
          
          <button
            onClick={handleAddGoal}
            style={{
              padding: '10px 20px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {currentGoal.name && goals.find(g => g.name === currentGoal.name) ? 'Update Goal' : 'Add Goal'}
          </button>
        </div>
      </div>

      {goals.length > 0 && (
        <div style={{ 
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h2>Your Investment Goals</h2>
          <p style={{ color: '#666', marginBottom: '15px' }}>
            Click on a goal card below to view and customize its recommended asset allocation. The allocation will be automatically calculated based on your goal parameters.
          </p>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '20px',
            marginBottom: '20px'
          }}>
            {goals.map((goal, index) => (
              <div
                key={index}
                onClick={() => handleSelectGoal(index)}
                style={{
                  padding: '15px',
                  backgroundColor: selectedGoalIndex === index ? '#e3f2fd' : 'white',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'background-color 0.2s'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '10px'
                }}>
                  <h3 style={{ margin: 0 }}>{goal.name}</h3>
                  <button
                    onClick={(e) => handleLoadGoal(e, goal)}
                    style={{
                      padding: '4px 12px',
                      backgroundColor: '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9em'
                    }}
                  >
                    Load
                  </button>
                </div>
                <div style={{ color: '#666' }}>
                  <div>Initial Amount: {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(goal.initialAmount)}</div>
                  {goal.targetAmount > 0 && (
                    <div>Target: {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(goal.targetAmount)}</div>
                  )}
                  {goal.monthlyContribution > 0 && (
                    <div>Monthly: {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(goal.monthlyContribution)}</div>
                  )}
                  <div>Time Horizon: {goal.timeHorizon} years</div>
                  <div>Risk Tolerance: {goal.riskTolerance}/10</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedGoalIndex !== null && (
        <div>
          <h2>Strategic Asset Allocation for {goals[selectedGoalIndex].name}</h2>
          <StrategicAssetAllocation
            assetClasses={assetClassData.assetClasses}
            allocation={goals[selectedGoalIndex].allocation}
            initialInvestment={goals[selectedGoalIndex].initialAmount}
            monthlyContribution={goals[selectedGoalIndex].monthlyContribution}
            targetAmount={goals[selectedGoalIndex].targetAmount}
            years={goals[selectedGoalIndex].timeHorizon}
            riskTolerance={goals[selectedGoalIndex].riskTolerance}
            username={username}
            onAllocationChange={handleAllocationChange}
          />
        </div>
      )}
    </div>
  );
};

export default GoalAnalysisPage;
